import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const PHASE_LABELS = {
  starting: 'Starting update...',
  uploading: 'Uploading firmware...',
  verifying: 'Waiting for device to finish writing to flash...',
  committing: 'Committing update...',
  rebooting: 'Device is rebooting...',
};

/**
 * Admin-only firmware flashing from a local .bin file. Streams an arbitrary
 * build to the connected device over the same Web Serial OTA path used for
 * published versions — useful for testing unreleased firmware.
 */
export default function FirmwareFileFlashCard({ onFlashFile, disabled }) {
  const [file, setFile] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [flashing, setFlashing] = useState(false);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = useCallback((event) => {
    const chosen = event.target.files?.[0] || null;
    setError(null);
    setSuccess(false);
    if (chosen && !chosen.name.toLowerCase().endsWith('.bin')) {
      setFile(null);
      setValidationError('Please choose a .bin firmware file.');
      return;
    }
    setValidationError(null);
    setFile(chosen);
  }, []);

  const handleProgress = useCallback((ph, received, total) => {
    setPhase(ph);
    setProgress(total > 0 ? Math.round((received / total) * 100) : 0);
  }, []);

  const handleFlash = useCallback(async () => {
    if (!file || !onFlashFile) return;
    setFlashing(true);
    setError(null);
    setSuccess(false);
    setPhase('starting');
    setProgress(0);

    try {
      await onFlashFile(file, handleProgress);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Firmware update failed');
    } finally {
      setFlashing(false);
      setPhase(null);
    }
  }, [file, onFlashFile, handleProgress]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Flash from File (admin)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Flash a local <code>.bin</code> build directly to the connected device.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
            Choose .bin File
            <input
              type="file"
              accept=".bin"
              hidden
              data-testid="firmware-file-input"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Typography variant="body2">
              {file.name}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          startIcon={<SystemUpdateAltIcon />}
          onClick={handleFlash}
          disabled={disabled || flashing || !file}
        >
          {flashing ? 'Flashing...' : 'Flash from File'}
        </Button>

        {flashing && phase && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {PHASE_LABELS[phase] || phase}
            </Typography>
            <LinearProgress
              variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
              value={progress}
            />
            {phase === 'uploading' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {progress}%
              </Typography>
            )}
          </Box>
        )}

        {validationError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {validationError}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Firmware flashed successfully! Device is rebooting.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

FirmwareFileFlashCard.propTypes = {
  onFlashFile: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
